import React from "react";

import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image,
} from "@react-pdf/renderer";


// =====================================================
// PRODUCTION IMAGE URL
// =====================================================

const getImageUrl = (image) => {

    if (!image) {
        return null;
    }

    let imageUrl = String(image).trim();

    if (!imageUrl) {
        return null;
    }

    if (imageUrl.startsWith("https://")) {
        return imageUrl;
    }

    if (imageUrl.startsWith("http://")) {

        if (
            typeof window !== "undefined" &&
            window.location.protocol === "https:"
        ) {

            return (
                "https://" +
                imageUrl.substring(7)
            );

        }

        return imageUrl;
    }

    if (imageUrl.startsWith("//")) {

        if (
            typeof window !== "undefined"
        ) {

            return (
                window.location.protocol +
                imageUrl
            );

        }

        return `https:${imageUrl}`;
    }

    if (imageUrl.startsWith("/")) {

        if (
            typeof window !== "undefined"
        ) {

            return (
                window.location.origin +
                imageUrl
            );

        }

        return imageUrl;
    }

    if (
        typeof window !== "undefined"
    ) {

        return (
            window.location.origin +
            "/" +
            imageUrl
        );

    }

    return imageUrl;
};


// =====================================================
// STYLES
// =====================================================

const styles = StyleSheet.create({

    page: {
        paddingTop: 28,
        paddingBottom: 30,
        paddingLeft: 24,
        paddingRight: 24,
        fontSize: 8.5,
        fontFamily: "Helvetica",
        backgroundColor: "#ffffff",
    },

    header: {
        marginBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: "#1f2937",
        paddingBottom: 9,
    },

    title: {
        fontSize: 19,
        fontWeight: "bold",
        color: "#111827",
        marginBottom: 5,
    },

    subtitle: {
        fontSize: 9,
        color: "#6b7280",
    },

    generatedDate: {
        fontSize: 8,
        color: "#6b7280",
        marginTop: 4,
    },

    summaryContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 15,
        gap: 7,
    },

    summaryBox: {
        flex: 1,
        borderWidth: 1,
        borderColor: "#d1d5db",
        borderRadius: 3,
        padding: 8,
    },

    summaryLabel: {
        fontSize: 7.5,
        color: "#6b7280",
        marginBottom: 4,
    },

    summaryValue: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#111827",
    },

    table: {
        width: "100%",
        borderWidth: 0.7,
        borderColor: "#9ca3af",
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#f3f4f6",
        borderBottomWidth: 0.7,
        borderBottomColor: "#9ca3af",
        minHeight: 27,
        alignItems: "center",
    },

    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 0.5,
        borderBottomColor: "#d1d5db",
        minHeight: 34,
        alignItems: "center",
    },

    tableRowLast: {
        flexDirection: "row",
        minHeight: 34,
        alignItems: "center",
    },

    cell: {
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 4,
        paddingBottom: 4,
        justifyContent: "center",
    },

    headerCell: {
        fontSize: 7.3,
        fontWeight: "bold",
        color: "#111827",
    },

    bodyCell: {
        fontSize: 7.6,
        color: "#374151",
    },

    imageColumn: {
        width: "6%",
    },

    idColumn: {
        width: "5%",
    },

    productColumn: {
        width: "16%",
    },

    typeColumn: {
        width: "8%",
    },

    quantityColumn: {
        width: "7%",
        textAlign: "center",
    },

    totalPiecesColumn: {
        width: "9%",
        textAlign: "center",
    },

    priceColumn: {
        width: "9%",
        textAlign: "right",
    },

    wholesaleColumn: {
        width: "10%",
        textAlign: "right",
    },

    sellingColumn: {
        width: "11%",
        textAlign: "right",
    },

    profitColumn: {
        width: "10%",
        textAlign: "right",
    },

    createdByColumn: {
        width: "11%",
    },

    dateColumn: {
        width: "8%",
    },

    productImage: {
        width: 24,
        height: 24,
        objectFit: "cover",
        borderRadius: 2,
    },

    imagePlaceholder: {
        width: 24,
        height: 24,
        backgroundColor: "#e5e7eb",
        borderRadius: 2,
    },

    footer: {
        position: "absolute",
        bottom: 12,
        left: 24,
        right: 24,
        flexDirection: "row",
        justifyContent: "space-between",
        borderTopWidth: 0.5,
        borderTopColor: "#d1d5db",
        paddingTop: 6,
    },

    footerText: {
        fontSize: 7.5,
        color: "#6b7280",
    },

});


// =====================================================
// FORMATTERS
// =====================================================

const money = (value) => {

    const numericValue =
        Number(value || 0);

    return `L$ ${numericValue.toLocaleString(
        undefined,
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    )}`;

};


const number = (value) => {

    return Number(value || 0).toLocaleString(
        undefined,
        {
            maximumFractionDigits: 0,
        }
    );

};


const dateFormat = (value) => {

    if (!value) {
        return "—";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "—";
    }

    return date.toLocaleDateString();

};


// =====================================================
// PRODUCT CALCULATIONS
// =====================================================

const getTotalUnits = (product) => {

    const quantity =
        Number(product.quantity || 0);

    if (
        product.unit_type === "carton"
    ) {

        const unitsPerCarton =
            Number(
                product.units_per_carton || 0
            );

        return (
            quantity *
            unitsPerCarton
        );
    }

    return quantity;
};


const getTotalStockSellingValue = (product) => {

    const quantity =
        Number(product.quantity || 0);

    const price =
        Number(product.price || 0);

    if (
        product.unit_type === "carton"
    ) {

        const unitsPerCarton =
            Number(
                product.units_per_carton || 0
            );

        if (unitsPerCarton <= 0) {
            return 0;
        }

        return (
            quantity *
            unitsPerCarton *
            price
        );
    }

    return (
        quantity *
        price
    );
};


const getTotalProfit = (product) => {

    const price =
        Number(product.price || 0);

    const wholesalePrice =
        Number(
            product.wholesale_price || 0
        );

    const quantity =
        Number(product.quantity || 0);

    if (
        product.unit_type !== "carton"
    ) {

        return (
            quantity *
            (
                wholesalePrice -
                price
            )
        );
    }

    const unitsPerCarton =
        Number(
            product.units_per_carton || 0
        );

    if (unitsPerCarton <= 0) {
        return 0;
    }

    const sellingValue =
        unitsPerCarton *
        price;

    return (
        sellingValue -
        wholesalePrice
    );
};


// =====================================================
// PRODUCT ROW
// =====================================================

const ProductRow = ({
    product,
    last = false,
}) => {

    const totalUnits =
        getTotalUnits(product);

    const totalStockSellingValue =
        getTotalStockSellingValue(product);

    const totalProfit =
        getTotalProfit(product);

    const imageUrl =
        getImageUrl(
            product.image
        );

    return (

        <View
            wrap={false}
            style={
                last
                    ? styles.tableRowLast
                    : styles.tableRow
            }
        >

            <View
                style={[
                    styles.cell,
                    styles.imageColumn,
                ]}
            >

                {imageUrl ? (

                    <Image
                        src={imageUrl}
                        style={
                            styles.productImage
                        }
                    />

                ) : (

                    <View
                        style={
                            styles.imagePlaceholder
                        }
                    />

                )}

            </View>


            <View
                style={[
                    styles.cell,
                    styles.idColumn,
                ]}
            >

                <Text
                    style={
                        styles.bodyCell
                    }
                >
                    {product.id}
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.productColumn,
                ]}
            >

                <Text
                    style={[
                        styles.bodyCell,
                        {
                            fontWeight:
                                "bold",
                        },
                    ]}
                >
                    {
                        product.name ||
                        "—"
                    }
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.typeColumn,
                ]}
            >

                <Text
                    style={
                        styles.bodyCell
                    }
                >
                    {
                        product.unit_type ===
                        "carton"
                            ? "Carton"
                            : "Single"
                    }
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.quantityColumn,
                ]}
            >

                <Text
                    style={
                        styles.bodyCell
                    }
                >
                    {
                        number(
                            product.quantity
                        )
                    }
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.totalPiecesColumn,
                ]}
            >

                <Text
                    style={[
                        styles.bodyCell,
                        {
                            fontWeight:
                                "bold",
                        },
                    ]}
                >
                    {
                        number(
                            totalUnits
                        )
                    }
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.priceColumn,
                ]}
            >

                <Text
                    style={
                        styles.bodyCell
                    }
                >
                    {
                        money(
                            product.price
                        )
                    }
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.wholesaleColumn,
                ]}
            >

                <Text
                    style={
                        styles.bodyCell
                    }
                >
                    {
                        money(
                            product.wholesale_price
                        )
                    }
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.sellingColumn,
                ]}
            >

                <Text
                    style={
                        styles.bodyCell
                    }
                >
                    {
                        money(
                            totalStockSellingValue
                        )
                    }
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.profitColumn,
                ]}
            >

                <Text
                    style={[
                        styles.bodyCell,
                        {
                            fontWeight:
                                "bold",
                        },
                    ]}
                >
                    {
                        money(
                            totalProfit
                        )
                    }
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.createdByColumn,
                ]}
            >

                <Text
                    style={
                        styles.bodyCell
                    }
                >
                    {
                        product.created_by
                            ?.username ||
                        "—"
                    }
                </Text>

            </View>


            <View
                style={[
                    styles.cell,
                    styles.dateColumn,
                ]}
            >

                <Text
                    style={
                        styles.bodyCell
                    }
                >
                    {
                        dateFormat(
                            product.created_at
                        )
                    }
                </Text>

            </View>

        </View>

    );

};


// =====================================================
// PDF
// =====================================================

const ProductListStockDetailsPDF = ({
    products = [],
}) => {

    const totalProducts =
        products.length;

    const totalStockValue =
        products.reduce(
            (
                total,
                product
            ) => {

                return (
                    total +
                    getTotalStockSellingValue(
                        product
                    )
                );

            },
            0
        );

    const totalProfit =
        products.reduce(
            (
                total,
                product
            ) => {

                return (
                    total +
                    getTotalProfit(
                        product
                    )
                );

            },
            0
        );


    return (

        <Document>

            <Page
                size="A4"
                orientation="landscape"
                style={styles.page}
            >

                <View
                    style={
                        styles.header
                    }
                >

                    <Text
                        style={
                            styles.title
                        }
                    >
                        Zuluwhiskey Stock Listing
                    </Text>


                    <Text
                        style={
                            styles.subtitle
                        }
                    >
                        Complete Product Stock Details
                    </Text>


                    <Text
                        style={
                            styles.generatedDate
                        }
                    >
                        Generated:{" "}
                        {
                            new Date()
                                .toLocaleString()
                        }
                    </Text>

                </View>


                <View
                    style={
                        styles.summaryContainer
                    }
                >

                    <View
                        style={
                            styles.summaryBox
                        }
                    >

                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            TOTAL PRODUCTS
                        </Text>


                        <Text
                            style={
                                styles.summaryValue
                            }
                        >
                            {
                                number(
                                    totalProducts
                                )
                            }
                        </Text>

                    </View>


                    <View
                        style={
                            styles.summaryBox
                        }
                    >

                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            TOTAL SELLING VALUE
                        </Text>


                        <Text
                            style={
                                styles.summaryValue
                            }
                        >
                            {
                                money(
                                    totalStockValue
                                )
                            }
                        </Text>

                    </View>


                    <View
                        style={
                            styles.summaryBox
                        }
                    >

                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            TOTAL PROFIT
                        </Text>


                        <Text
                            style={
                                styles.summaryValue
                            }
                        >
                            {
                                money(
                                    totalProfit
                                )
                            }
                        </Text>

                    </View>

                </View>


                <View
                    style={
                        styles.table
                    }
                >

                    <View
                        style={
                            styles.tableHeader
                        }
                        fixed
                    >

                        <View
                            style={[
                                styles.cell,
                                styles.imageColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                IMG
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.idColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                ID
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.productColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                PRODUCT
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.typeColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                TYPE
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.quantityColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                QTY
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.totalPiecesColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                TOTAL PCS
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.priceColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                PRICE
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.wholesaleColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                WHOLESALE
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.sellingColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                SELLING VALUE
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.profitColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                PROFIT
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.createdByColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                CREATED BY
                            </Text>

                        </View>


                        <View
                            style={[
                                styles.cell,
                                styles.dateColumn,
                            ]}
                        >

                            <Text
                                style={
                                    styles.headerCell
                                }
                            >
                                DATE
                            </Text>

                        </View>

                    </View>


                    {
                        products.map(
                            (
                                product,
                                index
                            ) => (

                                <ProductRow
                                    key={
                                        product.id ??
                                        index
                                    }
                                    product={
                                        product
                                    }
                                    last={
                                        index ===
                                        products.length - 1
                                    }
                                />

                            )
                        )
                    }

                </View>


                <View
                    style={
                        styles.footer
                    }
                    fixed
                >

                    <Text
                        style={
                            styles.footerText
                        }
                    >
                        Zuluwhiskey Stock Listing
                    </Text>


                    <Text
                        style={
                            styles.footerText
                        }
                        render={({
                            pageNumber,
                            totalPages,
                        }) =>
                            `Page ${pageNumber} of ${totalPages}`
                        }
                    />

                </View>

            </Page>

        </Document>

    );

};


export default ProductListStockDetailsPDF;